/**
 * Upload limits and their user-facing copy — the ONE place both live (AIVA-7072).
 *
 * Three limits guard chat uploads, and they exist for different reasons:
 *
 *  - MAX_FILE_BYTES — per file. Keeps one document from dominating the prompt
 *    (the backend injects extracted text into every turn). The Outlook spec's
 *    DR-01 AC-6 requires a single 5 MB attachment on a turn to be accepted, so
 *    this must never drop below 5,000,000 bytes and the comparison is
 *    strictly-greater-than.
 *
 *  - MAX_SESSION_FILES — per chat. Bounds how many distinct files accumulate
 *    across turns (the whole retained set is re-sent on every turn).
 *
 *  - SESSION_RAW_BYTE_BUDGET — per chat, in raw bytes. The gateway rejects
 *    requests over ~10 MB with HTTP 413 (measured empirically: 9 MB passes,
 *    11 MB is refused). Attachments ride the request base64-encoded (raw × 4/3)
 *    on EVERY turn, alongside the message and email/document context — so the
 *    retained set's raw bytes must stay far enough under the cap that a turn
 *    can never 413. 6 MiB raw ≈ 8.4 MB encoded, leaving headroom for the
 *    envelope. A single spec-mandated 5 MB file (≈6.7 MB encoded) always fits.
 *
 * Every rejection message is exported next to the number it describes so the
 * copy can't drift from the enforcement again — that drift (frontend saying
 * "5 MB", the 413 path claiming "up to 10 MB") is exactly the bug this module
 * closes. Copy rule per spec G-13: no emoji in product UI.
 */

export const MAX_FILE_BYTES = 5 * 1024 * 1024;

export const MAX_SESSION_FILES = 10;

export const SESSION_RAW_BYTE_BUDGET = 6 * 1024 * 1024;

export const FILE_TOO_LARGE_MESSAGE = `Each file must be ${MAX_FILE_BYTES / (1024 * 1024)} MB or smaller.`;

export const SESSION_BUDGET_MESSAGE =
  "Attached files exceed this chat's size limit. Remove a file or start a new chat.";

export const TOO_MANY_FILES_MESSAGE = `You can attach up to ${MAX_SESSION_FILES} files per chat. Start a new chat to add more.`;

/** Copy for a server 413 — the request as a whole was too large. Names the real
 *  cause (the accumulated set, not "a file") and the real remedy. The old copy
 *  claimed a "10 MB" per-file limit that never existed on any layer. */
export const REQUEST_TOO_LARGE_MESSAGE =
  "Your attachments are too large to send together. Remove a file or start a new chat, then try again.";
